//#region tokens/colors.ts
/**
* EITELKEIT Color Tokens
* ═══════════════════════════════════════════════════════════════════
* The Berghain Terminal aesthetic - dark, industrial, toxic mint glow
*/
const colors = {
	void: "#0a0a0b",
	voidDeep: "#050506",
	voidLight: "#111113",
	voidLighter: "#18181b",
	surface: "#1c1c1f",
	surfaceElevated: "#232326",
	surfaceOverlay: "rgba(28, 28, 31, 0.8)",
	mint: "#39ff9f",
	mintBase: "#39ff9f",
	mintDim: "#2dd882",
	mintGlow: "rgba(57, 255, 159, 0.15)",
	mintGlowStrong: "rgba(57, 255, 159, 0.3)",
	success: "#39ff9f",
	error: "#ff4757",
	errorBase: "#ff4757",
	red: "#ff4757",
	warning: "#ffb839",
	warningGlow: "rgba(255, 184, 57, 0.2)",
	danger: "#ff4757",
	dangerGlow: "rgba(255, 71, 87, 0.15)",
	info: "#39c0ff",
	infoGlow: "rgba(57, 192, 255, 0.15)",
	sub: "#a855f7",
	subGlow: "rgba(138, 43, 226, 0.2)",
	vip: "#e91e8c",
	vipGlow: "rgba(233, 30, 140, 0.15)",
	mintGlowSubtle: "rgba(57, 255, 159, 0.06)",
	text: "#f4f4f5",
	textPrimary: "#f4f4f5",
	textSecondary: "#a1a1aa",
	textMuted: "#71717a",
	border: "rgba(255, 255, 255, 0.06)",
	borderHover: "rgba(255, 255, 255, 0.1)",
	borderActive: "rgba(57, 255, 159, 0.3)",
	glassBg: "rgba(28, 28, 31, 0.6)",
	glassBorder: "rgba(255, 255, 255, 0.08)"
};
//#endregion
//#region tokens/focus.ts
/**
* EITELKEIT Focus Tokens
* ═══════════════════════════════════════════════════════════════════
* Focus ring styles for WCAG 2.4.7 Focus Visible compliance
* Ensures 3:1 contrast ratio for focus indicators
*/
const focusColors = {
	/** Primary mint focus color */
	primary: colors.mint,
	/** Danger/error focus color */
	danger: "#ff4757",
	/** Warning focus color */
	warning: "#ffb839",
	/** Info focus color */
	info: "#39c0ff",
	/** Light focus for dark backgrounds */
	light: "#ffffff",
	/** Dark focus for light backgrounds */
	dark: "#0a0a0b"
};
/**
* Focus ring using box-shadow (recommended approach)
* Benefits: doesn't affect layout, works with border-radius
*/
const focusRings = {
	/** Standard focus ring - mint glow */
	default: `0 0 0 2px ${colors.void}, 0 0 0 4px ${colors.mint}`,
	/** Strong focus ring - more prominent */
	strong: `0 0 0 2px ${colors.void}, 0 0 0 4px ${colors.mint}`,
	/** Subtle focus ring - less intrusive */
	subtle: `0 0 0 2px ${colors.mint}40`,
	/** Inset focus ring - for filled elements */
	inset: `inset 0 0 0 2px ${colors.mint}`,
	/** Danger focus ring - for error states */
	danger: `0 0 0 2px ${colors.void}, 0 0 0 4px ${focusColors.danger}`,
	/** Warning focus ring */
	warning: `0 0 0 2px ${colors.void}, 0 0 0 4px ${focusColors.warning}`,
	/** Info focus ring */
	info: `0 0 0 2px ${colors.void}, 0 0 0 4px ${focusColors.info}`,
	/** Light focus ring - for dark elements */
	light: `0 0 0 2px ${colors.void}, 0 0 0 4px ${focusColors.light}`,
	/** Glow focus ring - with soft glow effect */
	glow: `0 0 0 2px ${colors.void}, 0 0 0 4px ${colors.mint}, 0 0 12px ${colors.mint}40`,
	/** None - no focus ring */
	none: "none"
};
/**
* Focus using CSS outline (alternative approach)
* Benefits: native browser support, simple
*/
const focusOutlines = {
	/** Standard outline */
	default: {
		outline: `2px solid ${colors.mint}`,
		outlineOffset: "2px"
	},
	/** Strong outline - thicker */
	strong: {
		outline: `3px solid ${colors.mint}`,
		outlineOffset: "2px"
	},
	/** Danger outline */
	danger: {
		outline: `2px solid ${focusColors.danger}`,
		outlineOffset: "2px"
	},
	/** No outline */
	none: {
		outline: "none",
		outlineOffset: "0"
	}
};
/**
* Styles to apply only on keyboard focus (focus-visible)
* Prevents focus rings on mouse click while maintaining keyboard accessibility
*/
const focusVisibleStyles = {
	/** Hide default outline, show custom ring on focus-visible */
	base: { outline: "none" },
	/** Focus-visible state */
	visible: { boxShadow: focusRings.default },
	/** Combined CSS for components */
	css: `
    outline: none;

    &:focus-visible {
      box-shadow: ${focusRings.default};
    }
  `
};
/**
* Selectors for focusable elements (used in focus traps)
*/
const focusableSelectors = [
	"a[href]",
	"area[href]",
	"button:not([disabled])",
	"input:not([disabled]):not([type=\"hidden\"])",
	"select:not([disabled])",
	"textarea:not([disabled])",
	"[tabindex]:not([tabindex=\"-1\"])",
	"[contenteditable=\"true\"]"
].join(", ");
/**
* Get all focusable elements within a container
*/
function getFocusableElements(container) {
	return Array.from(container.querySelectorAll(focusableSelectors));
}
/**
* Get first focusable element
*/
function getFirstFocusable(container) {
	return container.querySelector(focusableSelectors);
}
/**
* Get last focusable element
*/
function getLastFocusable(container) {
	const elements = getFocusableElements(container);
	return elements[elements.length - 1] || null;
}
const componentFocus = {
	/** Button focus */
	button: {
		primary: focusRings.strong,
		ghost: focusRings.strong,
		danger: focusRings.danger,
		link: focusRings.subtle
	},
	/** Input focus */
	input: {
		default: focusRings.default,
		error: focusRings.danger
	},
	/** Checkbox/Switch focus */
	toggle: focusRings.strong,
	/** Tab focus */
	tab: focusRings.default,
	/** Card focus */
	card: focusRings.subtle,
	/** Menu item focus */
	menuItem: focusRings.inset,
	/** Modal close button */
	modalClose: focusRings.light
};
/**
* CSS-in-JS styles for focus indicators
*/
const focusIndicatorStyles = {
	/** Ring indicator (absolute positioned) */
	ring: {
		position: "absolute",
		inset: "-4px",
		borderRadius: "inherit",
		border: `2px solid ${colors.mint}`,
		pointerEvents: "none"
	},
	/** Underline indicator */
	underline: {
		position: "absolute",
		bottom: 0,
		left: 0,
		right: 0,
		height: "2px",
		background: colors.mint,
		borderRadius: "1px"
	},
	/** Dot indicator */
	dot: {
		width: "6px",
		height: "6px",
		borderRadius: "50%",
		background: colors.mint
	}
};
//#endregion
export { focusRings as a, getFirstFocusable as c, colors as d, focusOutlines as i, getFocusableElements as l, focusColors as n, focusVisibleStyles as o, focusIndicatorStyles as r, focusableSelectors as s, componentFocus as t, getLastFocusable as u };

//# sourceMappingURL=focus-DaIphPOd.mjs.map